import * as AuthActions from './auth.actions'
import * as ActivityFeedLikesActions from 'libs/gb-front/activity-feed/data-access-activity-feed-likes/src/lib/+state/activity-feed-likes.actions'
import * as AuctionActions from 'libs/gb-front/auction/data-access/src/lib/+state/auction.actions'
import * as EventTicketPackageProfileActivitiesActions from 'libs/gb-front/event-tickets/data-access-event-ticket-package-profile-activities/src/lib/+state/event-ticket-package-profile-activities.actions'
import * as EventTicketsActions from 'libs/gb-front/event-tickets/data-access/src/lib/+state/event-tickets.actions'
import * as BaseEventTicketCheckInActions from 'libs/shared/event-tickets/data-access-event-ticket-check-ins/src/lib/+state/base-event-ticket-check-ins.actions'
import * as FavouriteItemsActions from 'libs/gb-front/item/data-access-favourite-items/src/lib/+state/favourite-items.actions'
import * as GbRealtimeActions from 'libs/gb-front/core/util/src/lib/gb-realtime/gb-realtime.actions'
import * as InvoiceActions from 'libs/gb-front/checkout/data-access/src/lib/+state/invoice.actions'
import * as LoaderActions from 'libs/shared/utils/loader/src/lib/+state/loader.actions'
import * as ProfilesActions from 'libs/gb-front/profile/data-access/src/lib/+state/profiles.actions'
import * as ProfileItemActivitiesActions from 'libs/gb-front/item/data-access-profile-item-activities/src/lib/+state/profile-item-activities.actions'
import * as RafflePurchasesActions from 'libs/gb-front/item/data-access-raffle-purchases/src/lib/+state/raffle-purchases.actions'
import * as SelectedProfileActions from 'libs/gb-front/selected-profile/data-access/src/lib/+state/selected-profile.actions'
import * as VouchersActions from 'libs/gb-front/item/data-access-vouchers/src/lib/+state/vouchers.actions'

import { HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Action } from '@ngrx/store'
import { AuctionFacade } from 'libs/gb-front/auction/data-access/src/lib/+state/auction.facade'
import { AuthConstants } from 'libs/gb-front/auth/feature/login/src/lib/gb-front-auth-feature-login-routing.module'
import { GbAuthDialogComponent } from 'libs/gb-front/auth/util-auth-dialog/src/lib/gb-auth-dialog/gb-auth-dialog.component'
import { GbVerifyIdentityService } from 'libs/gb-front/auth/util/src'
import { GbFrontDialogService } from 'libs/gb-front/shared/util/src/lib/gb-front-dialog-service/gb-front-dialog.service'
import { LoaderActionTypeEnum } from 'libs/shared/utils/loader/src'
import { GbLoaderService } from 'libs/shared/utils/loader/src/lib/services/gb-loader.service'
import { GbNotificationService } from 'libs/shared/utils/notification/src/lib/service/gb-notification.service'
import {
  catchError,
  concatMap,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators'
import { AuthApiService } from './auth-api.service'
import { AuthFacade } from './auth.facade'
import { AuthDialogResultEnum, RegisterResponse, VerifyExtras } from './auth.models'
import { GbVerifyDialogComponent } from 'libs/gb-front/auth/feature/verify/src/lib/gb-verify-dialog/gb-verify-dialog.component'
import { of, zip } from 'rxjs'
import { GbLoginToProceedDialogComponent } from 'libs/gb-front/auth/util-auth-dialog/src/lib/gb-login-to-proceed-dialog/gb-login-to-proceed-dialog.component'
import { NoopScrollStrategy } from '@angular/cdk/overlay'
import { GbFrontCookieService } from 'libs/gb-front/auth/util/src'
import { GbErrorMessageService } from 'libs/shared/utils/helpers/src'
import { GbRegisterDialogComponent } from 'libs/gb-front/auth/feature/register/src/lib/gb-register-dialog/gb-register-dialog.component'
import { VerificationMethodEnum } from 'libs/gb-front/shared/models/shared.models'

@Injectable()
export class AuthEffects {
  syncCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.syncCurrentUser),
      withLatestFrom(this.authFacade.isAuthenticated$, this.auctionFacade.auction$),
      switchMap(([action, isAuthenticated, auction]) => {
        const tokenIsAuthed = this.gbFrontCookieService.isAuthenticated()
        const isInvalidVolunteer = this.gbFrontCookieService.isInvalidVolunteer(auction.uuid)
        const currentPath = window.location.pathname.split('/').pop()

        // logout if volunteer not part of the auction or
        // token cookie has been removed but state has values
        if (isInvalidVolunteer || (isAuthenticated && !tokenIsAuthed)) {
          return [AuthActions.logout()]
        }

        // do not sync profile on volunteer login page
        if (currentPath === AuthConstants.VOLUNTEER) {
          return []
        }

        // try to sync only if there is a token
        if (tokenIsAuthed) {
          return this.authService.getCurrentAuthProfile(action.urlName).pipe(
            map((result) => {
              const payload = {
                authentication: {
                  token: this.gbFrontCookieService.getTokenCookie(),
                  profile: result,
                },
                redirectHome: action.redirectHome,
              }

              return AuthActions.setAuthentication(payload)
            }),
            catchError(async (error: HttpErrorResponse) => AuthActions.syncCurrentUserFailure({ error })),
          )
        }
        return []
      }),
    ),
  )

  openRegisterLoginDialog$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AuthActions.openRegisterLoginDialog),
      exhaustMap((action) => {
        // modal that uses google recaptcha can show the challenge decentralized
        // setting the modal as NoopScrollStrategy helps that
        const dialogConfig = {
          scrollStrategy: new NoopScrollStrategy(),
          disableClose: true,
          data: action.registerExtras,
        }
        return zip([of(action), this.gbFrontDialogService.getDialogObs(GbAuthDialogComponent, dialogConfig)])
      }),
      filter(([_, result]) => !!result),
      map(([action, result]) => {
        // on register success
        if (result === AuthDialogResultEnum.RegisterSuccess) {
          return AuthActions.openRegisterVerifyDialog(action.onSuccess, action.registerExtras)
        }
        // on login success
        else if (!action.checkProfile && action.onSuccess) {
          // profile check not needed (i.e. create/like post, contact host)
          return action.onSuccess()
        } else {
          // default to check profile on login success
          return SelectedProfileActions.checkProfile(action.onSuccess)
        }
      }),
    )
  })

  openRegisterVerifyDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.openRegisterVerifyDialog),
      exhaustMap((action) => {
        const data: VerifyExtras = {
          code_only: action.registerExtras?.verification_method === VerificationMethodEnum.Code,
        }
        return zip([of(action), this.gbFrontDialogService.getDialogObs(GbVerifyDialogComponent, { data })])
      }),
      filter(([, result]) => !!result),
      map(([action]) => SelectedProfileActions.checkProfile(action.onSuccess)),
    ),
  )

  checkPromptLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.checkPromptLogin),
      withLatestFrom(this.authFacade.isAuthenticated$),
      filter(([_, isAuthenticated]) => {
        const tokenIsAuthed = this.gbFrontCookieService.isAuthenticated()
        return !isAuthenticated && !tokenIsAuthed // Filter out the action if already authenticated
      }),
      map(([action, _]) => {
        return AuthActions.openLoginToProceedDialog({ title: action.title })
      }),
    ),
  )

  openLoginToProceedDialog$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.openLoginToProceedDialog),
      exhaustMap((action) => {
        return zip([
          of(action),
          this.gbFrontDialogService.getDialogObs(GbLoginToProceedDialogComponent, {
            data: {
              title: action.title,
            },
          }),
        ])
      }),
      filter(([_, result]) => !result),
      map(() => AuctionActions.redirectHomePage()),
    ),
  )

  loginWithShortLink$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginWithShortLink),
      withLatestFrom(this.authFacade.token$),
      switchMap(([action, token]) =>
        this.authService.getCurrentAuthProfile(action.urlName).pipe(
          map((authProfile) =>
            AuthActions.loginSuccess({
              authentication: {
                token: token,
                profile: authProfile,
              },
            }),
          ),
          catchError(async (error: HttpErrorResponse) => AuthActions.loginFailed({ error })),
        ),
      ),
    ),
  )

  register$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.register),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      tap(() => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.Register, 'LOADER.SUBMITTING')),
      mergeMap(([action, urlName]) =>
        this.authService.register(urlName, action.payload).pipe(
          map((response: RegisterResponse) =>
            AuthActions.registerSuccess({
              identity: action.payload.identity,
              uuid: response.uuid,
              dialog: action.dialog,
            }),
          ),
          catchError(async (error: HttpErrorResponse) => AuthActions.registerFailure({ error })),
        ),
      ),
    ),
  )

  registerByVolunteer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.registerByVolunteer),
      withLatestFrom(this.auctionFacade.auctionUrlName$, this.authFacade.authentication$),
      tap(() => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.Register, 'LOADER.SUBMITTING')),
      mergeMap(([action, urlName, authentication]) =>
        this.authService
          .register(urlName, {
            ...action.payload,
            delegate: authentication.profile.profile,
          })
          .pipe(
            map((response: RegisterResponse) =>
              AuthActions.registerByVolunteerSuccess({
                identity: action.payload.identity,
                uuid: response.uuid,
                dialog: action.dialog,
              }),
            ),
            catchError(async (error: HttpErrorResponse) => AuthActions.registerFailure({ error })),
          ),
      ),
    ),
  )

  registerByVolunteerSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.registerByVolunteerSuccess),
      withLatestFrom(this.authFacade.isVerificationEnabled$),
      switchMap(([action, verificationEnabled]) => {
        const actions: Action[] = []

        if (!verificationEnabled) {
          actions.push(
            AuthActions.verify({
              payload: { identity: action.identity },
              dialog: action.dialog,
            }),
          )
        }
        return actions
      }),
    ),
  )

  closeVolunteerRegisterDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.registerByVolunteerSuccess),
        withLatestFrom(this.authFacade.isVerificationEnabled$),
        map(([action, isVerificationEnabled]) => {
          if (action.dialog) {
            const result = isVerificationEnabled ? 'verify' : 'skip-verify'
            this.gbFrontDialogService.closeDialog(GbRegisterDialogComponent, result)
          }
        }),
      ),
    { dispatch: false },
  )

  routeToVerifyVolunteer$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.registerByVolunteerSuccess),
        withLatestFrom(this.auctionFacade.auction$, this.authFacade.isVerificationEnabled$),
        map(([action, auction, isVerificationEnabled]) => {
          if (!action.dialog && isVerificationEnabled) this.router.navigate([auction.url_name, AuthConstants.VERIFY])
        }),
      ),
    { dispatch: false },
  )

  closeRegisterLoginDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.registerSuccess, AuthActions.loginSuccess),
        map((action) => {
          let dialogResult = ''
          if (action.type === AuthActions.registerSuccess.type) {
            dialogResult = AuthDialogResultEnum.RegisterSuccess
          }
          if (action.type === AuthActions.loginSuccess.type) {
            dialogResult = AuthDialogResultEnum.LoginSuccess
          }

          this.gbFrontDialogService.closeDialog(GbAuthDialogComponent, dialogResult)
        }),
      ),
    { dispatch: false },
  )

  closeLoginToProceedDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess, AuthActions.socialLoginSuccess),
        map(() => {
          this.gbFrontDialogService.closeDialog(GbLoginToProceedDialogComponent, true)
        }),
      ),
    { dispatch: false },
  )

  hideRegisterLoaderDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.registerSuccess, AuthActions.registerByVolunteerSuccess, AuthActions.registerFailure),
        map(() => {
          this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.Register)
        }),
      ),
    { dispatch: false },
  )

  setIdentity$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.registerSuccess, AuthActions.registerByVolunteerSuccess),
        tap((action) => this.gbVerifyIdentityService.setIdentity(action.identity, action.uuid)),
      ),
    { dispatch: false },
  )

  routeToVerify$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.registerSuccess),
        withLatestFrom(this.auctionFacade.auction$),
        map(([action, auction]) => {
          if (!action.dialog) this.router.navigate([auction.url_name, AuthConstants.VERIFY])
        }),
      ),
    { dispatch: false },
  )

  registerFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.registerFailure),
        map((action) => {
          const error = action.error

          if (error.error) {
            return this.gbNotificationService.error(error.error, 'ok')
          }

          return this.gbNotificationService.error('AUTH.REGISTER.GENERIC_ERROR', 'ok')
        }),
      ),
    { dispatch: false },
  )

  routeAfterVerify$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.verifySuccess, AuthActions.socialLoginSuccess),
        withLatestFrom(this.auctionFacade.auctionUrlName$, this.auctionFacade.requiresAdditionalInfo$),
        tap(([action, urlName, requiresAdditionalInfo]) => {
          if (action.dialog) return
          if (
            (action.authentication.profile.additional_info_required && requiresAdditionalInfo) ||
            action.authentication.profile.password_required
          ) {
            this.router.navigate([urlName, AuthConstants.ADDITIONAL_INFO])
            return
          }
          if (action.type !== AuthActions.socialLoginSuccess.type) {
            this.router.navigate([urlName, AuthConstants.REGISTER_SUCCESS], {
              queryParams: { complete: true },
            })
            return
          }
        }),
      ),
    { dispatch: false },
  )

  login$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.login),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      mergeMap(([action, urlName]) =>
        this.authService.appLogin(urlName, action.identity, action.password).pipe(
          map((authentication) =>
            AuthActions.loginSuccess({
              authentication: authentication,
              redirectHome: action.redirectHome,
              dialog: action.dialog,
            }),
          ),
          catchError(async (error: HttpErrorResponse) => AuthActions.loginFailed({ error })),
        ),
      ),
    ),
  )

  loginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.loginSuccess),
      map((action) =>
        AuthActions.setAuthentication({
          authentication: action.authentication,
          redirectHome: action.redirectHome,
        }),
      ),
    ),
  )

  loginFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginFailed),
        tap((action) => {
          const errorMsg = this.gbErrorMessageService.getErrorMessage(action.error.error)
          if (errorMsg) this.gbNotificationService.error(errorMsg)
        }),
      ),
    { dispatch: false },
  )

  volunteerLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.volunteerLogin),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      mergeMap(([action, urlName]) =>
        this.authService.appVolunteerLogin(urlName, action.payload).pipe(
          map((authentication) => AuthActions.volunteerLoginSuccess({ authentication })),
          catchError(async (error: HttpErrorResponse) => AuthActions.loginFailed({ error })),
        ),
      ),
    ),
  )

  volunteerLoginSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.volunteerLoginSuccess),
      map((action) =>
        AuthActions.setAuthentication({
          authentication: action.authentication,
          redirectHome: true,
        }),
      ),
    ),
  )

  setAuthentication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.setAuthentication),
      tap((action) => this.gbFrontCookieService.setTokenCookie(action.authentication.token)),
      switchMap((action) => {
        const isGuest = this.gbFrontCookieService.isGuest()

        const actions: Action[] = [AuthActions.setAuthenticationSuccess()]

        if (isGuest) {
          actions.push(
            SelectedProfileActions.setGuestProfile({
              profile: action.authentication.profile,
            }),
          )
        }

        if (action.redirectHome) {
          actions.push(AuctionActions.redirectHomePage())
        }

        return actions
      }),
    ),
  )

  showLoginLoaderDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginWithShortLink, AuthActions.login, AuthActions.socialLogin, AuthActions.volunteerLogin),
        map(() => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.Login)),
      ),
    { dispatch: false },
  )

  hideLoginLoaderDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.loginSuccess, AuthActions.loginFailed, AuthActions.volunteerLoginSuccess),
        map(() => this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.Login)),
      ),
    { dispatch: false },
  )

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      switchMap((action) => {
        return this.authService.logout().pipe(
          map(() => AuthActions.logoutSuccess(action.redirectHome)),
          catchError(async () => AuthActions.logoutSuccess(action.redirectHome)),
        )
      }),
    ),
  )

  logoutSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logoutSuccess),
      tap(() => this.gbFrontCookieService.clearTokenCookie()),
      switchMap((action) => {
        const actions: Action[] = [
          ProfilesActions.clearProfiles(),
          SelectedProfileActions.clearSelectedProfile(),
          FavouriteItemsActions.clearFavouriteItems(),
          ActivityFeedLikesActions.clearActivityFeedLikes(),
          RafflePurchasesActions.clearRafflePurchases(),
          EventTicketsActions.clearEventTickets(),
          EventTicketPackageProfileActivitiesActions.clearEventTicketPackageProfileActivities(),
          BaseEventTicketCheckInActions.clearEventTicketCheckIns(),
          LoaderActions.clearActionsInProgress(),
          VouchersActions.clearVouchers(),
          ProfileItemActivitiesActions.clearProfileItemActivities(),
          InvoiceActions.clearInvoice(),
          GbRealtimeActions.unsubscribePrivateChannel(),
        ]

        if (action.redirectHome) {
          actions.push(AuctionActions.redirectHomePage())
        }

        return actions
      }),
    ),
  )

  refreshToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshToken),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      switchMap(([, urlName]) =>
        this.authService.refreshAuthToken(urlName).pipe(
          map((authentication) => AuthActions.refreshTokenSuccess({ authentication })),
          catchError(async (error: HttpErrorResponse) => AuthActions.refreshTokenFailure({ error })),
        ),
      ),
    ),
  )

  refreshTokenSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshTokenSuccess),
      map(({ authentication }) => AuthActions.setAuthentication({ authentication })),
    ),
  )

  refreshTokenFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshTokenFailure),
      map(() => AuthActions.logout()),
    ),
  )

  /**
   *init the private pusher channels after the success login set the cookies
   */
  setAuthenticationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.setAuthenticationSuccess),
      mergeMap(() => {
        const actions: Action[] = []
        if (this.gbFrontCookieService.isVolunteer()) {
          actions.push(GbRealtimeActions.initPrivateVolunteerChannel())
        } else {
          actions.push(GbRealtimeActions.initPrivateAuctionProfileChannel())
        }
        return actions
      }),
    ),
  )

  socialLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.socialLogin),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      mergeMap(([action, urlName]) =>
        this.authService.appSocialLogin(urlName, action.accessToken, action.provider).pipe(
          switchMap((authentication) => {
            const redirectHome = !authentication.profile.additional_info_required && action.redirectHome

            const actions: Action[] = [
              AuthActions.loginSuccess({
                authentication: authentication,
                redirectHome: redirectHome,
                dialog: action.dialog,
              }),
              AuthActions.socialLoginSuccess({
                authentication: authentication,
                dialog: action.dialog,
              }),
            ]

            if (action.onSuccess) actions.push(action.onSuccess)

            return actions
          }),
          catchError(async (error: HttpErrorResponse) => AuthActions.loginFailed({ error })),
        ),
      ),
    ),
  )

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.forgotPassword),
      tap(() => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.ForgotPassword)),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      switchMap(([action, urlName]) =>
        this.authService.forgotPassword(urlName, action.payload).pipe(
          map(() => AuthActions.forgotPasswordSuccess()),
          catchError(async () => AuthActions.forgotPasswordFailure()),
        ),
      ),
    ),
  )

  closeForgotPasswordLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.forgotPasswordSuccess, AuthActions.forgotPasswordFailure),
        tap(() => this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.ForgotPassword)),
      ),
    { dispatch: false },
  )

  showForgotPasswordSuccessMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.forgotPasswordSuccess),
        tap(() => {
          this.gbNotificationService.success('AUTH.FORGOT_PASSWORD.SUCCESS_MESSAGE')
        }),
      ),
    { dispatch: false },
  )

  showForgotPasswordErrorMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.forgotPasswordFailure),
        tap(() => {
          this.gbNotificationService.error('AUTH.FORGOT_PASSWORD.ERROR_MESSAGE')
        }),
      ),
    { dispatch: false },
  )

  verifyForgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifyForgotPassword),
      tap(() => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.VerifyForgotPassword)),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      switchMap(([action, urlName]) =>
        this.authService.verifyForgotPassword(urlName, action.payload).pipe(
          map((response) =>
            AuthActions.verifyForgotPasswordSuccess({
              authentication: response.authentication,
            }),
          ),
          catchError(async () => AuthActions.verifyForgotPasswordFailure()),
        ),
      ),
    ),
  )

  hideVerifyForgotPasswordLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.verifyForgotPasswordFailure, AuthActions.verifyForgotPasswordSuccess),
        tap(() => {
          this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.VerifyForgotPassword)
        }),
      ),
    { dispatch: false },
  )

  onResetPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.verifyForgotPasswordSuccess),
      tap(() => this.gbNotificationService.success('AUTH.RESET_PASSWORD.SUCCESS')),
      switchMap((action) => [
        // set token cookies and authentication on reset password
        AuthActions.loginSuccess({ authentication: action.authentication }),
        // redirect to homepage
        AuctionActions.redirectHomePage(),
      ]),
    ),
  )

  notifyOnVerifyForgotPasswordError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.verifyForgotPasswordFailure),
        tap(() => this.gbNotificationService.error('AUTH.RESET_PASSWORD.ERROR')),
      ),
    { dispatch: false },
  )

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changePassword),
      tap(() => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.ProfileUpdate, 'LOADER.SAVING')),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      switchMap(([action, auctionUrlName]) => {
        return this.authService.changePassword(auctionUrlName, action.payload).pipe(
          map(() =>
            AuthActions.changePasswordSuccess({
              onSuccess: action.onSuccess,
            }),
          ),
          catchError(async (error) =>
            AuthActions.changePasswordFailure({
              error: error.error.non_field_errors[0],
            }),
          ),
        )
      }),
    ),
  )

  changePasswordFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.changePasswordFailure),
        map((action) => this.gbNotificationService.error(action.error.error)),
      ),
    { dispatch: false },
  )

  hideProfileUpdateLoaderDialog$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.changePasswordSuccess, AuthActions.changePasswordFailure),
        map(() => {
          this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.ProfileUpdate)
        }),
      ),
    { dispatch: false },
  )

  showSnackbarOnUpdateSelectedProfileSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.changePasswordSuccess),
        map((action) => {
          this.gbNotificationService.success('PROFILE_UPDATE_SUCCESS')
          if (action.onSuccess) action.onSuccess()
        }),
      ),
    { dispatch: false },
  )

  resendPhoneNumberVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.resendPhoneNumberVerification),
      tap(() => this.loaderService.showLoaderDialog(LoaderActionTypeEnum.ResendSmsVerification)),
      withLatestFrom(this.auctionFacade.auctionUrlName$),
      mergeMap(([action, urlName]) =>
        this.authService.resendPhoneNumberVerification(urlName, action.profileUuid).pipe(
          map(() => AuthActions.resendPhoneNumberVerificationSuccess()),
          catchError(async (error: HttpErrorResponse) => AuthActions.resendPhoneNumberVerificationFailure({ error })),
        ),
      ),
    ),
  )

  resendPhoneNumberVerificationSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.resendPhoneNumberVerificationSuccess),
        switchMap(() => [this.gbNotificationService.success('ACCOUNT.DETAILS_FORM.SMS_VERIFICATION_SENT')]),
      ),
    { dispatch: false },
  )

  resendPhoneNumberVerificationFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.resendPhoneNumberVerificationFailure),
        map((action) => this.gbNotificationService.error(action.error.error)),
      ),
    { dispatch: false },
  )

  hideResendPhoneNumberVerificationLoader$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.resendPhoneNumberVerificationSuccess, AuthActions.resendPhoneNumberVerificationFailure),
        map(() => this.loaderService.hideLoaderDialog(LoaderActionTypeEnum.ResendSmsVerification)),
      ),
    { dispatch: false },
  )

  constructor(
    private actions$: Actions,
    private authService: AuthApiService,
    private auctionFacade: AuctionFacade,
    private authFacade: AuthFacade,
    private loaderService: GbLoaderService,
    private router: Router,
    private gbVerifyIdentityService: GbVerifyIdentityService,
    private gbNotificationService: GbNotificationService,
    private gbFrontDialogService: GbFrontDialogService,
    private gbFrontCookieService: GbFrontCookieService,
    private gbErrorMessageService: GbErrorMessageService,
  ) {}
}
